import React, { useEffect, useState } from 'react';
import { Button, ButtonToolbar, Card, CardBody, Col } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { KKuTuIOApi } from '../../../KKuTuIOApi';
import { BulkWordCheckbox, BulkWordNotice } from './BulkWordCommon';
import BulkWordDeleteConfirmModal from './BulkWordDeleteConfirmModal';
import 'react-toastify/dist/ReactToastify.css';
import './BulkWord.scss';

const BulkWordDeletePage = ({ lang, languageName }) => {
  const navigate = useNavigate();
  const [wordText, setWordText] = useState('');
  const [isSaving, setSaving] = useState(false);
  const [isUpdateLogIgnore, setUpdateLogIgnore] = useState(false);
  const [isUpdateLogIncludeDetail, setUpdateLogIncludeDetail] = useState(false);
  const [preview, setPreview] = useState(null);
  const [request, setRequest] = useState(null);
  const [isOpenModal, setOpenModal] = useState(false);
  const wordCount = wordText.split('\n').map(word => word.trim()).filter(Boolean).length;

  const showToast = (level, message) => toast[level](message, {
    position: 'top-center', autoClose: 10000, closeOnClick: true, pauseOnHover: true, draggable: true
  });

  const handlePreview = () => {
    const words = wordText.split('\n').map(word => word.trim()).filter(Boolean);
    if (words.length === 0) {
      showToast('error', '삭제할 단어를 한 줄에 하나씩 입력해 주세요.');
      return;
    }

    const requestData = {
      words,
      updateLogIgnore: isUpdateLogIgnore,
      updateLogIncludeDetail: isUpdateLogIncludeDetail
    };
    setSaving(true);
    KKuTuIOApi.post(`/words/${lang}/bulk-delete/preview`, requestData)
      .then(({ data }) => {
        setSaving(false);
        if (!data.success) {
          showToast('error', `${data.resultMessage} (${data.resultCode})`);
          return;
        }
        setRequest(requestData);
        setPreview(data.data);
        setOpenModal(true);
      })
      .catch(error => {
        setSaving(false);
        showToast('error', `대량 삭제 사전 확인 중 오류가 발생했습니다: ${error.message || error}`);
      });
  };

  const handleUpdateLogIgnore = () => {
    setUpdateLogIgnore(!isUpdateLogIgnore);
    if (!isUpdateLogIgnore) setUpdateLogIncludeDetail(false);
  };

  useEffect(() => {
    const handleShortcut = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (!isSaving) handlePreview();
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  });

  return (
    <>
      <ToastContainer position="top-center" autoClose={10000} newestOnTop closeOnClick draggable pauseOnHover/>
      <Col md={12} lg={12}>
        <Card className="bulk-word-page">
          <CardBody>
            <h1 className="text-center mb-3">{languageName} 단어 대량 삭제</h1>
            <BulkWordNotice color="danger" className="mb-5 bulk-word-page__notice">
              <span>저장하시기 전 변경 사항을 다시 한번 확인해 주세요. 모든 작업은 자동으로 기록되며 작업자 추적이 가능합니다. Ctrl/⌘+S로 삭제 대상을 확인할 수 있습니다.</span>
            </BulkWordNotice>
            <form className="form form--horizontal">
              <div className="form__form-group">
                <span className="form__form-group-label">삭제할 단어</span>
                <div className="form__form-group-field col-9">
                  <div className="bulk-word-page__input-wrap">
                    <textarea className="bulk-word-textarea" name="words" rows="10"
                              placeholder="한 줄에 한 단어씩 입력하세요."
                              value={wordText} onChange={event => setWordText(event.target.value)}/>
                    <div className="bulk-word-page__input-meta">
                      <span>빈 줄과 반복 입력은 확인 단계에서 정리됩니다.</span>
                      {wordCount > 0 && <strong>{wordCount}개 입력</strong>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="form__form-group">
                <span className="form__form-group-label">단어 업데이트 내역</span>
                <div className="form__form-group-field col-9">
                  <div className="bulk-word-log-options">
                    <BulkWordCheckbox id="bulk-delete-log-ignore"
                                      label="이 작업을 숨깁니다. (추가/수정/삭제 횟수가 집계되지 않습니다)"
                                      checked={isUpdateLogIgnore} onChange={handleUpdateLogIgnore}/>
                    <BulkWordCheckbox id="bulk-delete-log-detail"
                                      label="이 작업의 세부 내용을 포함시킵니다. (예시: 과자/고양이 등)"
                                      disabled={isUpdateLogIgnore} checked={isUpdateLogIncludeDetail}
                                      onChange={() => setUpdateLogIncludeDetail(!isUpdateLogIncludeDetail)}/>
                  </div>
                </div>
              </div>
              <ButtonToolbar className="form__button-toolbar">
                <Button type="button" color="danger" onClick={handlePreview} disabled={isSaving}>
                  {isSaving ? '확인 중...' : '삭제 대상 확인'}
                </Button>
                <Button type="button" onClick={() => navigate(`/words-${lang}`)} disabled={isSaving}>작업 취소</Button>
              </ButtonToolbar>
            </form>
          </CardBody>
        </Card>
      </Col>
      <BulkWordDeleteConfirmModal isOpen={isOpenModal} lang={lang} preview={preview} request={request}
          onToggle={() => setOpenModal(!isOpenModal)} onShowToast={showToast}
          onComplete={() => navigate(`/words-${lang}`)}/>
    </>
  );
};

export default BulkWordDeletePage;
